import * as React from 'react'
import { To } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import { Box, Spinner } from '@factorialco/deprecated-design-system'
import { useDocumentsDataTable } from 'generated/resources/datatable.graphql'
import { DataTableFeatureFlagDocument } from 'generated/resources/graphql'

import DataTable from 'modules/core/components/DataTable'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import ContextFromPage from 'modules/documents/components/DatatableContextFromPage/ContextFromPage'
import { useGetFolders } from 'modules/documents/hooks/useGetFolders'
import { SPACES } from 'modules/documents/lib/constants'
import type { SPACE } from 'modules/documents/lib/constants'
import {
  deletedAtFilter,
  deletedByFilter,
  employeeFilter,
  folderFilter,
} from 'modules/documents/lib/datatable/documents/filters'
import {
  actionsProjection,
  contentTypeProjection,
  deletedAtProjection,
  employeeProjection,
  filenameProjection,
  folderProjection,
} from 'modules/documents/lib/datatable/documents/projections'

import Actions from './_actions'
import BulkActions from './_bulk_actions'

const ITEMS_PER_PAGE = 100

interface Props {
  space: SPACE
  parentRoute: To
  getItemRoute: (documentId: number) => To
}

const TrashBinDataTable = ({ space, parentRoute, getItemRoute }: Props) => {
  const companyId = useCurrentSession().company?.id
  const { data: featureData } = useQuery(DataTableFeatureFlagDocument, {
    variables: { companyId: companyId ?? 0 },
    skip: companyId == null,
  })
  const deleteFeatureEnabled =
    featureData?.features?.featuresConnection?.nodes?.[0]?.enabled ?? false

  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const { folders, loading: loadingFolders } = useGetFolders({
    space,
  })
  const isLoading = loadingFolders
  const isEmployeeSpace = space === SPACES.EMPLOYEE_MY_DOCUMENTS

  const projections = [
    contentTypeProjection(),
    filenameProjection(),
    ...(isEmployeeSpace ? [employeeProjection()] : []),
    deletedAtProjection(),
    folderProjection(),
    actionsProjection({
      render: (document) => (
        <Actions
          getItemRoute={getItemRoute}
          document={document}
          deleteFeatureEnabled={deleteFeatureEnabled}
        />
      ),
    }),
  ]

  const source = useDocumentsDataTable(projections, {
    variables: { byTrashBin: true, space, search: debouncedSearch },
    identifier: `trash_bin_${space}`,
  })

  const filters = [
    ...(isEmployeeSpace ? [employeeFilter(source, users)] : []),
    folderFilter(source, folders),
    deletedByFilter(source, users.models),
    deletedAtFilter(source),
  ]

  if (isLoading)
    return (
      <Box alignItems="center" padding="s64">
        <Spinner />
      </Box>
    )

  return (
    <DataTable.Provider
      source={source}
      enableClearFilters
      filters={filters}
      pagination={{
        kind: 'offset',
        itemsPerPage: ITEMS_PER_PAGE,
      }}
      sort={{
        path: 'deletedAt',
        direction: 'desc',
      }}
    >
      <Box paddingY="s8" gap="s16">
        <DataTable.HeadBar
          search={{ value: search, onChange: onSearch, placeholder: i18n.t('common.search') }}
          actions={<BulkActions deleteFeatureEnabled={deleteFeatureEnabled} />}
          disableDefaultActions
        />
      </Box>
      <DataTable.Table selectable={() => true} />
      <ContextFromPage parentRoute={parentRoute} />
    </DataTable.Provider>
  )
}

export default TrashBinDataTable
